const get = require('lodash/get')
const { getUnknownBatchID } = require('../../shipment/tools/virtual-batch')
const { parse } = require('../../tools/smart-id')
const { parseBatchId } = require('../../shipment/tools/product-batch-util')

// TODO: Once return reasons are configurable, wire this through from config
const DEFAULT_RETURN_REASON = 'DAMAGED'
const DEFAULT_EXPIRY = '3050-12-31'

/**
 * Build Shipsy return-shipment items for a single product/sku.
 *
 * - If `count.batches` is present, we create one line per batch.
 * - Otherwise, we fall back to a single "virtual" batch using the aggregate `count.quantity`.
 *
 * @param {string} productSku - SKU code for the product
 * @param {Object} count - Count information, optionally including `batches`
 * @param {Object} batchDocs - Map of batchId -> batch document (with `expiry`)
 * @returns {Array<Object>} Array of item lines for this sku
 */
const buildBatchItems = (productSku, count = {}, batchDocs = {}) => {
  const batches = Array.isArray(count.batches) ? count.batches : []

  // When there are explicit batches on the count, generate a line per batch
  if (batches.length > 0) {
    return batches.map(batch => {
      const batchId = get(batch, 'batchId') || getUnknownBatchID(`product:${productSku}`)

      // Use the original batchId on the count to look up batch metadata/expiry
      const batchDoc = batchDocs[batch.batchId]
      const expiryDate = get(batchDoc, 'expiry', DEFAULT_EXPIRY).split('T')[0] // YYYY-MM-DD

      return {
        sku: productSku,
        quantity: get(batch, 'quantity', 0),
        returnReason: DEFAULT_RETURN_REASON,
        batchId,
        expiryDate
      }
    })
  }

  // No batch breakdown: fall back to a single line with a virtual batch
  const virtualBatchId = getUnknownBatchID(`product:${productSku}`)

  return [{
    sku: productSku,
    quantity: get(count, 'quantity', 0),
    returnReason: DEFAULT_RETURN_REASON,
    batchId: virtualBatchId,
    expiryDate: DEFAULT_EXPIRY
  }]
}

/**
 * Convert all counted products into Shipsy shipment items.
 *
 * We also normalise batch IDs to the plain `batchNo` expected by Shipsy.
 *
 * @param {Object} counts - Map of productSku -> count object
 * @param {Object} batchDocs - Map of batchId -> batch document with `expiry`
 * @returns {Array<Object>} Flattened array of shipment items for all products
 */
const generateShipmentItems = (counts, batchDocs = {}) => {
  return Object.keys(counts).reduce((items, productSku) => {
    const batchItems = buildBatchItems(productSku, counts[productSku], batchDocs).map(item => {
      const { batchNo } = parseBatchId(item.batchId)

      return {
        ...item,
        // Shipsy expects the plain batch number, not the smart-id encoded value
        batchId: batchNo
      }
    })

    items.push(...batchItems)
    return items
  }, [])
}

/**
 * Generate a Shipsy-compatible return shipment payload from a stock-count shipment.
 *
 * @param {Object} shipment - Original shipment object coming from SL
 * @param {Object} locationById - Map of locationId -> location object
 * @param {Object} batchDocs - Map of batchId -> batch document with `expiry`
 * @returns {Object} Payload ready to send to Shipsy
 */
const generateReturnShipment = (shipment, locationById, batchDocs = {}) => {
  const items = generateShipmentItems(shipment.counts, batchDocs)

  // Shipsy expects a YYYY-MM-DD date string, derived from `createdAt`
  const d = new Date(shipment.createdAt)
  const shipmentDate = d.toISOString().split('T')[0]
  const shipmentNumber = shipment.shipmentNumber
  const {shipmentNo: parentShipmentNumber} = parse(shipment.parentDocId || '')

  return {
    origin: get(locationById[shipment.originId], 'uuid'),
    destination: get(locationById[shipment.destinationId], 'code'),
    shipmentDate,
    orderId: parentShipmentNumber || shipmentNumber,
    shipmentType: 'return',
    items,
    shipmentNo: shipmentNumber
  }
}

module.exports = generateReturnShipment
